import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SearchResult, UserProfile } from '../types';
import { searchContent, getCurrentUser } from '../services/api';
import { useToast } from '../context/ToastContext';
import './SearchPage.css';

const SearchPage: React.FC = () => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [totalResults, setTotalResults] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  
  // Define performSearch with useCallback
  const performSearch = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 3) {
      setErrorMessage('Please enter at least 3 characters to search');
      return;
    }
    
    try {
      setIsSearching(true);
      setErrorMessage(null);
      
      // Update URL with search query without triggering a reload
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('query', searchQuery);
      navigate({
        pathname: location.pathname,
        search: searchParams.toString()
      }, { replace: true });
      
      const response = await searchContent(searchQuery);
      setResults(response.results);
      setTotalResults(response.results_count);
      setHasSearched(true);
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to perform search');
      setResults([]);
    } finally {
      setIsSearching(false);
      setIsLoading(false);
    }
  }, [location.pathname, location.search, navigate]);
  
  // Parse query parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryParam = searchParams.get('query');
    
    if (queryParam && queryParam.length >= 3) {
      setQuery(queryParam);
      performSearch(queryParam);
    } else {
      setIsLoading(false);
    }
    
    // Check authentication
    const checkAuth = async () => {
      try {
        const userData = await getCurrentUser();
        setUser(userData);
        
        if (!userData.authenticated) {
          // Show toast explaining the redirect
          if (userData.tokenPresent) {
            addToast({
              type: 'warning',
              title: 'Session Invalid',
              message: 'Your authentication token is present but invalid. Please log in again.',
              duration: 5000
            });
          } else {
            addToast({
              type: 'error',
              title: 'Not Authenticated',
              message: 'Session cookies not found. Please log in to continue.',
              duration: 5000
            });
          }
          // Delay the redirect slightly to allow toast to be seen
          setTimeout(() => navigate('/login'), 1000);
        }
      } catch (err) {
        // If API call failed, show network error toast
        addToast({
          type: 'error',
          title: 'Authentication Error',
          message: 'Could not verify your authentication status. Network error or API unavailable.',
          duration: 5000
        });
        // Delay the redirect slightly to allow toast to be seen
        setTimeout(() => navigate('/login'), 1000);
      }
    };
    
    checkAuth();
  }, [location.search, navigate, performSearch, addToast]);
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(query);
  };
  
  const formatTimeDisplay = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  const renderResultMetadata = (result: SearchResult) => {
    if (result.start_page !== undefined && result.end_page !== undefined) {
      return (
        <div className="result-pages">Pages {result.start_page} - {result.end_page}</div>
      );
    } else if (result.start_time !== undefined && result.end_time !== undefined) {
      return (
        <div className="result-time">
          {formatTimeDisplay(result.start_time)} - {formatTimeDisplay(result.end_time)}
        </div>
      );
    }
    return null;
  };
  
  if (isLoading) {
    return <div className="app-container loading">Loading...</div>;
  }
  
  return (
    <div className="search-page">
      <header className="app-header">
        <h1>Search Your Documents</h1>
        {user?.authenticated && (
          <div className="header-controls">
            <p className="user-info">Signed in as: {user.name || user.email || user.userId}</p>
            <a href="/logout" className="button logout-button">Logout</a>
          </div>
        )}
      </header>
      
      <main className="search-content">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-container">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for content (min. 3 characters)"
              className="search-input"
              required
              minLength={3}
            />
            <button 
              type="submit" 
              className="search-button"
              disabled={query.length < 3 || isSearching}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>
          
          {errorMessage && (
            <div className="search-error">
              {errorMessage}
            </div>
          )}
        </form>
        
        {results.length > 0 ? (
          <div className="search-results">
            <h2>Results ({totalResults})</h2>
            
            <div className="results-list">
              {results.map((result, index) => (
                <div key={`${result.genesis_task_id}-${result.chunk_index}-${index}`} className="result-item">
                  <div className="result-score">
                    {parseFloat(result.similarity_score).toFixed(2)}
                  </div>
                  <div className="result-content">
                    <div className="result-text" dangerouslySetInnerHTML={{ __html: result.extraction_text }} />
                    <div className="result-meta">
                      <a 
                        href={result.google_drive_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="result-link"
                      >
                        View in Google Drive
                      </a>
                      {renderResultMetadata(result)}
                      <div className="result-task">
                        Task: <a href={`/task/${result.genesis_task_id}`}>{result.genesis_task_id}</a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : hasSearched && !isSearching && !errorMessage ? (
          <div className="no-results">
            No results found for "{query}"
          </div>
        ) : !hasSearched && !isSearching ? (
          <div className="search-instructions">
            Enter a search term above to find content in your documents.
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default SearchPage; 